import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { routes } from '@/config/routes.config.tsx';
import { I18nextProvider } from 'react-i18next';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import i18n from './i18n/i18n.config';
import * as Sentry from '@sentry/react';
import { getHealthCheckQueryOptions } from '@/features/maintenance/healthCheckQuery';
import { getMaintenanceStatusQueryOptions } from '@/features/maintenance/maintenanceStatusQuery';

const mode = import.meta.env.MODE;

if (mode === 'stage' || mode === 'prod') {
	const dsn = import.meta.env.VITE_SENTRY_DSN;
	if (dsn) {
		const environment = mode === 'prod' ? 'production' : 'staging';
		const release = import.meta.env.VITE_SENTRY_RELEASE;

		Sentry.init({
			dsn,
			environment,
			...(release ? { release } : {}),
			sendDefaultPii: true,
			integrations: [
				// Sentry.replayIntegration(), // uncomment to re-enable Session Replay
			],
			replaysSessionSampleRate: 0,
			replaysOnErrorSampleRate: 0,

			beforeSend(event, hint) {
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				const error: any = hint?.originalException;

				if (error?.response?.status === 401) {
					return null;
				}

				if (error?.status === 401) {
					return null;
				}

				try {
					const html = document.documentElement;
					const translatePresent =
						html.classList.contains('translated-ltr') ||
						html.classList.contains('translated-rtl');
					event.tags = {
						...(event.tags ?? {}),
						translatePresent: String(translatePresent),
					};
				} catch {
					// Non-browser or restricted context; ignore.
				}

				return event;
			},
		});
	}
}

const queryClient = new QueryClient();

void queryClient.prefetchQuery(getHealthCheckQueryOptions());
void queryClient.prefetchQuery(getMaintenanceStatusQueryOptions());

const router = createBrowserRouter(routes);

createRoot(document.getElementById('root')!).render(
	<StrictMode>
		<I18nextProvider i18n={i18n}>
			<QueryClientProvider client={queryClient}>
				<RouterProvider router={router} />
			</QueryClientProvider>
		</I18nextProvider>
	</StrictMode>,
);
